/**
 * Command-admission predicates for site configuration writes.
 *
 * Contains:
 * - camera, sensor, intrusion, relay, device, settings, and feature predicates
 * - relation-presence compatibility for plain and legacy Immutable entities
 *
 * Returns plain JS — no JSX, redux, or network I/O.
 */

const SENSOR_METADATA_FIELDS = new Set(['key', 'sensor_type', 'sensor_number', 'name', 'editable_fields']);
const INTRUSION_INPUT_METADATA_FIELDS = new Set(['key', 'name']);

const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value ?? {}, key);
const hasKeysOutside = (value, allowed) => Object.keys(value ?? {}).some(key => !allowed.has(key));
const alarmId = row => row?.alarm_id?.id ?? row?.alarm_id ?? null;

/** Return true/false when a relation field is present, or null when it is not represented. */
export function getKnownRelationPresence(entity, field) {
  if (entity == null) return null;
  if (typeof entity.has === 'function' && typeof entity.get === 'function') {
    return entity.has(field) ? entity.get(field) != null : null;
  }
  return hasOwn(entity, field) ? entity[field] != null : null;
}

export function cameraConfigRequiresCommandLease({
  operation,
  currentCamera = null,
  payload = {},
  cicHasRadio = null,
}) {
  if (cicHasRadio === false) return false;
  if (operation === 'create' || operation === 'delete') return true;
  if (operation !== 'patch' || !hasOwn(payload, 'motion_alarm_enabled')) return false;
  return Boolean(currentCamera?.motion_alarm_enabled) !== Boolean(payload.motion_alarm_enabled);
}

export function sensorConfigRequiresCommandLease(patch) {
  if (hasOwn(patch, 'common')) return true;
  return (patch?.sensors ?? []).some(sensorPatch => hasKeysOutside(sensorPatch, SENSOR_METADATA_FIELDS));
}

export function intrusionConfigRequiresCommandLease(patch) {
  if (hasOwn(patch, 'settings')) return true;
  return (patch?.inputs ?? []).some(inputPatch => hasKeysOutside(inputPatch, INTRUSION_INPUT_METADATA_FIELDS));
}

export function relayConfigRequiresCommandLease(payload, { hasRadio = true } = {}) {
  if (!hasRadio) return false;
  if (payload?.relaycontroller != null || payload?.relay_monitor != null) return true;
  return (payload?.actions ?? []).some(
    action =>
      (action?.schedules?.length ?? 0) > 0 ||
      (action?.relays?.length ?? 0) > 0 ||
      (action?.loads ?? []).some(
        load => load?.action === 'create' || load?.action === 'delete' || hasOwn(load, 'primary_relay_id'),
      ),
  );
}

/** Mirror the consolidated site-settings service's TIME_SET / ALARM_SET intents. */
export function siteSettingsRequiresCommandLease(
  currentSettings,
  payload,
  { hasRadio = false, hasStandaloneCic = false, alarmDefinitions = [] } = {},
) {
  if (
    (hasRadio || hasStandaloneCic) &&
    hasOwn(payload?.location, 'time_zone') &&
    String(currentSettings?.location?.time_zone ?? '') !== String(payload.location.time_zone ?? '')
  ) {
    return true;
  }

  if (hasRadio && payload?.weather != null) {
    const currentWeather = currentSettings?.weather ?? {};
    const invertChanged =
      hasOwn(payload.weather, 'invert_operation') &&
      currentWeather.invert_operation !== payload.weather.invert_operation;
    const relaysChanged =
      hasOwn(payload.weather, 'relays') &&
      JSON.stringify(currentWeather.relays) !== JSON.stringify(payload.weather.relays);
    if (invertChanged || relaysChanged) return true;
  }

  if (!hasRadio || !Array.isArray(payload?.alarms)) return false;

  const alarmDefinitionsById = new Map(alarmDefinitions.map(definition => [String(definition.id), definition]));
  const currentAlarmsById = new Map((currentSettings?.alarms ?? []).map(row => [String(alarmId(row)), row]));

  return payload.alarms.some(row => {
    const id = alarmId(row);
    if (id == null) return false;
    const current = currentAlarmsById.get(String(id));
    const definition = alarmDefinitionsById.get(String(id));
    const triggerSource = definition?.trigger_source ?? current?.trigger_source ?? row?.trigger_source;
    return (
      current != null &&
      (triggerSource == null || triggerSource === 'unit') &&
      Boolean(current.enabled) !== Boolean(row.enabled)
    );
  });
}

/** Site feature transitions whose handlers can submit or enqueue device work. */
export function siteFeatureRequiresCommandLease(featureKey, nextEnabled) {
  if (featureKey === 'INTRUSION') return true;
  if (featureKey === 'WEATHER') return !nextEnabled;
  return featureKey === 'DIMMING' && nextEnabled;
}

export function deviceConfigurationRequiresCommandLease(config, payload, { cicHasRadio = false } = {}) {
  const sections = new Set(Object.keys(payload ?? {}));
  if (config?.unit_type === 'vantage' && (sections.has('network') || sections.has('device'))) return true;
  if (config?.unit_type === 'next_gen' && sections.has('device')) return true;
  if (config?.unit_type === 'two_port_dc' && sections.has('thresholds')) return true;

  const cicPatch = payload?.cic;
  if (cicHasRadio === false || cicPatch == null || Object.keys(cicPatch).length === 0) return false;
  return Object.entries(cicPatch).some(([field, value]) => config?.values?.cic?.[field] !== value);
}
